/**
 * TDBExportMIS — one-click "📊 Export Full MIS to Excel" button.
 *
 * Reads selected doctrine from localStorage (synced with True P&L tile).
 * Period picker matches what's shown on the True P&L tile.
 * Streams the .xlsx download via the existing API endpoint.
 *
 * Gated to CEO + TDB-leader (Britto excluded server-side · button
 * hides if 403 returns from the preview endpoint).
 *
 * UX:
 *   • Picker: month
 *   • Tooltip: "Period: 2026-04 · Group GOP ₹1,07,825 · Doctrine: ScaleBoard"
 *   • Click → downloads · filename matches X-Filename header
 *   • Spinner during generation (< 5s by design)
 *   • Disabled state when no data for that period
 */
import { useEffect, useState, useCallback } from 'react';
import { Download, FileSpreadsheet, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/lib/api';

function thisMonthYM() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 7);
}

function fmtRupees(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export default function TDBExportMIS({ className = '' }) {
  const [period, setPeriod] = useState(thisMonthYM());
  const [doctrine, setDoctrine] = useState(() => {
    try {
      return window.localStorage.getItem('tdb_pnl_doctrine') === 'source' ? 'source' : 'scaleboard';
    } catch { return 'scaleboard'; }
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');
  const [allowed, setAllowed] = useState(true);

  // Pick up doctrine changes from sibling tile
  useEffect(() => {
    const handler = () => {
      try {
        const next = window.localStorage.getItem('tdb_pnl_doctrine');
        if (next && next !== doctrine) setDoctrine(next);
      } catch { /* noop */ }
    };
    window.addEventListener('focus', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.removeEventListener('storage', handler);
    };
  }, [doctrine]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/export/preview', {
        params: { period, doctrine },
      });
      setPreview(r.data);
      setAllowed(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setAllowed(false);
        setPreview(null);
      } else {
        setErr(e?.response?.data?.detail || 'Preview failed');
        setPreview(null);
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [period, doctrine]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const download = useCallback(async () => {
    setDownloading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/export/xlsx', {
        params: { period, doctrine },
        responseType: 'blob',
      });
      // Filename from Content-Disposition · falls back to preview filename
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : (preview?.filename || `tdb_mis_${period}.xlsx`);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Blob bodies on error need text() to read the JSON
      let detail = 'Download failed';
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          detail = JSON.parse(text)?.detail || detail;
        } catch { /* keep default */ }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      setErr(detail);
    } finally {
      setDownloading(false);
    }
  }, [period, doctrine, preview]);

  if (!allowed) {
    // Britto doesn't see the button (server-side 403). Render nothing.
    return null;
  }

  const available = !!preview?.available;
  const isOpen = !!preview?.is_open;
  const doctrineLabel = doctrine === 'scaleboard' ? '📐 ScaleBoard' : '📜 Source';
  const tooltip = available
    ? `Period: ${preview?.period}${isOpen ? '-MTD' : ''} · Group GOP ${fmtRupees(preview?.group_gop || 0)} (${(preview?.group_gop_pct || 0).toFixed(1)}%) · Doctrine: ${doctrineLabel.replace(/[📐📜] /, '')}`
    : preview?.message || 'No data for this period yet';

  return (
    <div
      data-testid="tdb-export-mis"
      className={`bg-white rounded-2xl border border-gray-200 p-4 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
          <h3 className="text-sm font-semibold text-gray-900">Export Full MIS to Excel</h3>
          <span
            data-testid="tdb-export-doctrine-badge"
            className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
              doctrine === 'source' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
            }`}
          >
            {doctrineLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="tdb-export-period-picker"
            type="month"
            value={period}
            max={thisMonthYM()}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          />
          <button
            data-testid="tdb-export-download-button"
            onClick={download}
            disabled={!available || downloading || previewLoading}
            title={tooltip}
            className={`text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1 ${
              available && !downloading
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {downloading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {downloading ? 'Generating…' : isOpen ? 'Export MTD .xlsx' : 'Export .xlsx'}
          </button>
        </div>
      </div>

      {preview && available && (
        <div
          data-testid="tdb-export-preview-strip"
          className="mt-2 text-[11px] text-gray-600 flex items-center justify-between flex-wrap gap-2"
        >
          <span>
            {isOpen ? '⚡ Live MTD' : '📁 Closed period'} ·{' '}
            <span className="font-mono">{preview.filename}</span>
          </span>
          <span>
            Group GOP{' '}
            <span className={`font-semibold ${preview.group_gop >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {fmtRupees(preview.group_gop || 0)} ({(preview.group_gop_pct || 0).toFixed(1)}%)
            </span>
          </span>
        </div>
      )}

      {!available && preview && (
        <div
          data-testid="tdb-export-no-data"
          className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-1.5 flex items-start gap-1.5"
        >
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          {preview.message || 'No data for this period · upload MIS Excel first'}
        </div>
      )}

      {err && (
        <div
          data-testid="tdb-export-error"
          className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md p-1.5"
        >
          {err}
        </div>
      )}
    </div>
  );
}
